<template>
	<div class="wrapper">
		<div v-if="!isDataLoaded" class="text-center">
			<b-spinner label="Loading..." style="width: 4rem; height: 4rem;" variant="info"></b-spinner>
		</div>
		<div v-else class="animated fadeIn">
			<b-row>
				<b-col cols="12">
					<div>
						<genericTable :table-data="tableData" :fields="fields" :filter="true" :lazy-filter-enabled="true"
													:per-page="searchParams.perPage" :search-method="searchPaymentHistory"
													:filter-and-pagination-status="filterAndPaginationStatus"
													hover striped bordered small
													:payment-history-status="paymentStatus"
													:caption="caption">
						<template v-slot:row-actions="row" v-if="paymentStatus === 0">
								<b-button size="sm" variant="primary" active v-b-modal.payment-history-editor @click="passDataToModal(row.row.item.id)" class="mr-1">
									<i class="icon-eye"></i> Review
								</b-button>
						</template>
						</genericTable>
						<template v-if="paymentStatus === 0">
							<b-modal id="payment-history-editor" title="Review Payment" @cancel="closeModal">
								<b-form @submit.prevent="" method="post">
									<b-form-group label="Project Name" :horizontal="true" label-for="projectName">
										<b-form-input name="projectName" id="projectName" type="text" v-model="selectedPaymentHistoryToEdit.project_name" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Bill Generation Date" :horizontal="true" label-for="invoiceNo">
										<b-form-input name="billGenerationDate" id="billGenerationDate" type="text" v-model="selectedPaymentHistoryToEdit.bill_generation_date" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Payment Date" :horizontal="true" label-for="paymentDate">
										<b-form-input name="paymentDate" id="paymentDate" type="text" v-model="selectedPaymentHistoryToEdit.payment_date" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Total Amount" :horizontal="true" label-for="totalAmount">
										<b-form-input name="totalAmount" id="totalAmount" type="number" v-model="selectedPaymentHistoryToEdit.total_amount" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Paid Amount" :horizontal="true" label-for="paidAmount">
										<b-form-input name="paidAmount" id="paidAmount" type="number" v-model="selectedPaymentHistoryToEdit.paid_amount" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Discount Amount" :horizontal="true" label-for="discountAmount">
										<b-form-input name="discountAmount" id="discountAmount" type="number" v-model="selectedPaymentHistoryToEdit.discount" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Current Due" :horizontal="true" label-for="currentDue">
										<b-form-input name="currentDue" id="currentDue" type="number" v-model="selectedPaymentHistoryToEdit.current_due" trim disabled></b-form-input>
									</b-form-group>
									<b-form-group label="Invoice No" :horizontal="true" label-for="invoiceNo">
										<model-select id="invoiceNo" :options="unpaidInvoiceList" v-model="selectedPaymentHistoryToEdit.invoice_id" placeholder="Please Select"></model-select>
										<b-form-invalid-feedback v-show="errors.has('invoiceNo')">{{errors.first('invoiceNo')}}</b-form-invalid-feedback>
									</b-form-group>
									<b-form-group label="Remarks" :horizontal="true" label-for="remarks">
										<b-form-textarea name="remarks" id="remarks" type="text" :state="!errors.has('remarks')" v-model="selectedPaymentHistoryToEdit.remarks" trim></b-form-textarea>
										<b-form-invalid-feedback v-show="errors.has('remarks')">{{errors.first('remarks')}}</b-form-invalid-feedback>
									</b-form-group>
								</b-form>
								<template #modal-footer>
									<b-button variant="outline-primary" @click="closeModal">Cancel</b-button>
									<b-button variant="danger" @click="rejectPaymentHistory">Reject Payment</b-button>
									<b-button variant="primary" @click="approvePaymentHistory">Approve Payment</b-button>
								</template>
							</b-modal>
						</template>
					</div>
				</b-col>
			</b-row>
		</div>
	</div>
</template>

<script>
import paymentService from "@/services/paymentService";
import toastService from "@/services/toastService";
import moment from "moment/moment";
import genericTable from "@/components/GenericTable.vue";
import invoiceService from "@/services/invoiceService";
import { required, numeric, minValue, maxLength } from 'vuelidate/lib/validators'
import {ModelSelect} from "vue-search-select";

export default {
	name: 'PaymentHistories',
	components: {
		genericTable,
		ModelSelect
	},
	props: {
		paymentStatus: {
			type: Number,
			required: true
		},
		unpaidInvoiceList : {
			type: Array,
			required: false,
			default() {
				return [];
			}
		},
		reloadMethod: {
			type: Function,
			required: false
		},
	},
	data: function () {
		return {
			isDataLoaded: false,
			searchParams : {
				perPage : 25,
				pageNumber : 0,
				searchKey : null,
				paymentStatus: this.paymentStatus
			},
			filterAndPaginationStatus: {
				totalPages: 0,
				currentPage: 0,
				searchKeyword: null
			},
			paymentHistoryList: [],
			selectedPaymentHistoryToEdit:{},
			fields: [
				{key: 'invoice_number', label: 'Invoice#', sortable: true},
				{key: 'project_name', label: 'Project Name', sortable: true},
				{key: 'bill_generation_date', label: 'Bill Generation Date', sortable: true},
				{key: 'payment_date', label: 'Payment Date', sortable: true},
				{key: 'total_amount', label: 'Total Amount', sortable: false},
				{key: 'paid_amount', label: 'Paid Amount', sortable: false},
				{key: 'discount', label: 'Discount', sortable: false},
				{key: 'current_due', label : 'Current Due', sortable : false},
				{key: 'remarks', label: 'Remarks', sortable: true},
				{key: 'actions', label: 'Actions', class : 'text-center'}
			],
		};
	},
	methods: {
		init: function () {
			this.isDataLoaded = false;
			Promise.all([this.getPaymentHistories()]).then(() => {
				this.isDataLoaded = true;
				this.deleteOrAddNecessaryTableElements();
			});
		},
		deleteOrAddNecessaryTableElements: function(){
			if(this.paymentStatus === 0){
				this.deleteFieldsWithKeys(['invoice_number','remarks']);
			}else{
				this.deleteFieldsWithKeys(['actions']);
			}
		},
		deleteFieldsWithKeys: function(unnecessaryColumns){
			unnecessaryColumns.forEach(v=>{
				this.fields.forEach((f,i)=>{
					if(f.key==v)this.fields.splice(i,1);
				})
			})
		},
		getPaymentHistories: function() {
			return paymentService.getPaymentHistoryList(this.searchParams,(data) => {
				this.filterAndPaginationStatus.totalPages = data.numberOfPages;
				this.filterAndPaginationStatus.currentPage = data.currentPage;
				this.filterAndPaginationStatus.searchKeyword = this.searchParams.searchKey;
				this.paymentHistoryList = this.createPaymentHistoryListTable(data.paymentHistoryList);
			}, (error) => {
				toastService.showErrorToast("Could not fetch payment histories" + error);
			});
		},
		createPaymentHistoryListTable: function(data){
			let historyList = [];
			data.forEach((value, index) => {
				let row = new Object();
				row.id = value.id;
				row.invoice_id = value.invoiceId;
				row.invoice_number = value.invoiceNumber;
				row.project_name = value.subscriberProjectName;
				row.bill_generation_date = moment(value.billGenerationDate).format('DD-MM-YYYY');
				row.payment_date = moment(value.paymentDate).format('DD-MM-YYYY');
				row.total_amount = value.totalAmount !== null ? value.totalAmount.toFixed(2) : "N/A";
				row.paid_amount = value.paidAmount !== null ? value.paidAmount.toFixed(2) : "N/A";
				row.discount = value.discountedAmount !== null? value.discountedAmount.toFixed(2) : "N/A";
				row.current_due = value.currentDue !== null ? value.currentDue.toFixed(2) : "N/A";
				row.remarks = value.remarks;
				historyList.push(row);
			});

			return historyList;
		},
		searchPaymentHistory : function (pageIndex, keyword) {
			this.setParams(keyword, pageIndex);
			this.getPaymentHistories();
		},
		setParams : function (keyword, pageIndex) {
			this.searchParams.searchKey = keyword;
			this.searchParams.pageNumber = pageIndex != null && !isNaN(pageIndex) ? pageIndex : 0;
		},
		approvePaymentHistory: function(){
			if(this.selectedPaymentHistoryToEdit.invoice_id == null){
				toastService.showErrorToast("Please Enter a valid invoice number");
				return;
			}
			var request = {
				paymentHistoryId : this.selectedPaymentHistoryToEdit.id,
				invoiceId        : this.selectedPaymentHistoryToEdit.invoice_id,
				approvalStatus   : 1,
				remarks 				 : this.selectedPaymentHistoryToEdit.remarks
			}
			paymentService.verifyPaymentForAnInvoice(request,(message)=>{
				toastService.showSuccessToast("Verified the payment successfully");
				this.reload();
			},(err)=>{
				toastService.showErrorToast("Error Occurred: " + err);
			})
		},
		rejectPaymentHistory: function(){
			var request = {
				paymentHistoryId : this.selectedPaymentHistoryToEdit.id,
				invoiceId        : this.selectedPaymentHistoryToEdit.invoice_id,
				approvalStatus   : -1,
				remarks 				 : this.selectedPaymentHistoryToEdit.remarks
			}
			paymentService.verifyPaymentForAnInvoice(request,(message)=>{
				toastService.showSuccessToast("Rejected the payment successfully")
				this.reload();
			},(err)=>{
				toastService.showErrorToast("Error Occurred: " + err)
			})
		},
		clearModalData: function(){
			this.selectedPaymentHistoryToEdit={}
		},
		closeModal: function() {
			this.$nextTick(() => {
				this.$bvModal.hide("payment-history-editor");
			});
			this.clearModalData();
		},
		passDataToModal: function(paymentHistoryId){
			this.selectedPaymentHistoryToEdit = JSON.parse(JSON.stringify(this.paymentHistoryList.filter(value => {return value.id === paymentHistoryId})[0]));
		},
		reload: function () {
			this.reloadMethod();
		}
	},
	created() {
		this.init();
	},
	computed: {
		tableData : function(){
			return this.paymentHistoryList;
		},
		caption: function () {
			if(this.paymentStatus === -1) return "<i class='icon-list'></i> Rejected Payment History";
			if(this.paymentStatus === 1) return "<i class='icon-list'></i> Approved Payment History";
			return "<i class='icon-list'></i> Pending Payment History";
		}
	}
}

</script>

<style scoped>

</style>
